"use client";

import { useLocale } from "next-intl";

import type { UiLanguage } from "@/lib/site/site-routing";

export type AiElementsCopy = {
  emptyTitle: string;
  emptyDescription: string;
  chainOfThought: string;
  close: string;
  source: string;
  image: string;
  attachment: string;
  remove: string;
  toggle: string;
  previousVersion: string;
  nextVersion: string;
  removeAttachment: string;
  thinking: string;
  thought: string;
  thoughtFor: (seconds: number) => string;
  urlPlaceholder: string;
  preview: string;
  console: string;
  noOutput: string;
  unknownSource: string;
  previous: string;
  next: string;
  contextUsage: string;
  totalCost: string;
  input: string;
  output: string;
  reasoning: string;
  cacheHit: string;
  uploadFile: string;
  promptPlaceholder: string;
  send: string;
  selectModel: string;
  toolStatus: Record<
    | "input-streaming"
    | "input-available"
    | "approval-requested"
    | "approval-responded"
    | "output-available"
    | "output-error"
    | "output-denied",
    string
  >;
  toolParameters: string;
  toolError: string;
  toolResult: string;
  sourcesUsed: (count: number) => string;
};

const COPY: Record<UiLanguage, AiElementsCopy> = {
  zh: {
    emptyTitle: "还没有消息",
    emptyDescription: "发一句话开始对话",
    chainOfThought: "思考过程",
    close: "关闭",
    source: "来源",
    image: "图片",
    attachment: "附件",
    remove: "移除",
    toggle: "展开或收起",
    previousVersion: "上一个版本",
    nextVersion: "下一个版本",
    removeAttachment: "移除附件",
    thinking: "正在思考…",
    thought: "已深度思考",
    thoughtFor: (seconds: number) => `已深度思考（用时 ${seconds} 秒）`,
    urlPlaceholder: "输入网址…",
    preview: "预览",
    console: "控制台",
    noOutput: "暂无输出",
    unknownSource: "未知来源",
    previous: "上一个",
    next: "下一个",
    contextUsage: "上下文用量",
    totalCost: "合计花费",
    input: "输入",
    output: "输出",
    reasoning: "推理",
    cacheHit: "其中缓存命中",
    uploadFile: "上传文件",
    promptPlaceholder: "有什么想问的？",
    send: "发送",
    selectModel: "选择模型",
    toolStatus: {
      "input-streaming": "准备中",
      "input-available": "调用中",
      "approval-requested": "待确认",
      "approval-responded": "已确认",
      "output-available": "已完成",
      "output-error": "调用失败",
      "output-denied": "已拒绝",
    },
    toolParameters: "参数",
    toolError: "错误信息",
    toolResult: "返回结果",
    sourcesUsed: (count: number) => `引用了 ${count} 个来源`,
  },
  en: {
    emptyTitle: "No messages yet",
    emptyDescription: "Send a message to start the conversation",
    chainOfThought: "Chain of thought",
    close: "Close",
    source: "Source",
    image: "Image",
    attachment: "Attachment",
    remove: "Remove",
    toggle: "Expand or collapse",
    previousVersion: "Previous version",
    nextVersion: "Next version",
    removeAttachment: "Remove attachment",
    thinking: "Thinking…",
    thought: "Thought deeply",
    thoughtFor: (seconds: number) => `Thought for ${seconds} seconds`,
    urlPlaceholder: "Enter a URL…",
    preview: "Preview",
    console: "Console",
    noOutput: "No output",
    unknownSource: "Unknown source",
    previous: "Previous",
    next: "Next",
    contextUsage: "Context usage",
    totalCost: "Total cost",
    input: "Input",
    output: "Output",
    reasoning: "Reasoning",
    cacheHit: "Cache hits",
    uploadFile: "Upload file",
    promptPlaceholder: "What would you like to ask?",
    send: "Send",
    selectModel: "Select a model",
    toolStatus: {
      "input-streaming": "Preparing",
      "input-available": "Running",
      "approval-requested": "Awaiting approval",
      "approval-responded": "Approved",
      "output-available": "Completed",
      "output-error": "Failed",
      "output-denied": "Denied",
    },
    toolParameters: "Parameters",
    toolError: "Error",
    toolResult: "Result",
    sourcesUsed: (count: number) => `Used ${count} sources`,
  },
  ja: {
    emptyTitle: "まだメッセージはありません",
    emptyDescription: "メッセージを送って会話を始めましょう",
    chainOfThought: "思考プロセス",
    close: "閉じる",
    source: "出典",
    image: "画像",
    attachment: "添付ファイル",
    remove: "削除",
    toggle: "開く／閉じる",
    previousVersion: "前のバージョン",
    nextVersion: "次のバージョン",
    removeAttachment: "添付ファイルを削除",
    thinking: "考え中…",
    thought: "じっくり考えました",
    thoughtFor: (seconds: number) => `じっくり考えました（${seconds} 秒）`,
    urlPlaceholder: "URL を入力…",
    preview: "プレビュー",
    console: "コンソール",
    noOutput: "出力はありません",
    unknownSource: "不明な出典",
    previous: "前へ",
    next: "次へ",
    contextUsage: "コンテキスト使用量",
    totalCost: "合計コスト",
    input: "入力",
    output: "出力",
    reasoning: "推論",
    cacheHit: "うちキャッシュヒット",
    uploadFile: "ファイルをアップロード",
    promptPlaceholder: "何を聞きたいですか？",
    send: "送信",
    selectModel: "モデルを選択",
    toolStatus: {
      "input-streaming": "準備中",
      "input-available": "実行中",
      "approval-requested": "承認待ち",
      "approval-responded": "承認済み",
      "output-available": "完了",
      "output-error": "失敗",
      "output-denied": "拒否",
    },
    toolParameters: "パラメーター",
    toolError: "エラー",
    toolResult: "結果",
    sourcesUsed: (count: number) => `${count} 件の出典を参照`,
  },
  "zh-tw": {
    emptyTitle: "還沒有訊息",
    emptyDescription: "傳一句話開始對話",
    chainOfThought: "思考過程",
    close: "關閉",
    source: "來源",
    image: "圖片",
    attachment: "附件",
    remove: "移除",
    toggle: "展開或收起",
    previousVersion: "上一個版本",
    nextVersion: "下一個版本",
    removeAttachment: "移除附件",
    thinking: "正在思考…",
    thought: "已深度思考",
    thoughtFor: (seconds: number) => `已深度思考（用時 ${seconds} 秒）`,
    urlPlaceholder: "輸入網址…",
    preview: "預覽",
    console: "主控台",
    noOutput: "暫無輸出",
    unknownSource: "未知來源",
    previous: "上一個",
    next: "下一個",
    contextUsage: "上下文用量",
    totalCost: "合計花費",
    input: "輸入",
    output: "輸出",
    reasoning: "推理",
    cacheHit: "其中命中快取",
    uploadFile: "上傳檔案",
    promptPlaceholder: "有什麼想問的？",
    send: "傳送",
    selectModel: "選擇模型",
    toolStatus: {
      "input-streaming": "準備中",
      "input-available": "呼叫中",
      "approval-requested": "待確認",
      "approval-responded": "已確認",
      "output-available": "已完成",
      "output-error": "呼叫失敗",
      "output-denied": "已拒絕",
    },
    toolParameters: "參數",
    toolError: "錯誤訊息",
    toolResult: "回傳結果",
    sourcesUsed: (count: number) => `引用了 ${count} 個來源`,
  },
  ko: {
    emptyTitle: "아직 메시지가 없습니다",
    emptyDescription: "메시지를 보내 대화를 시작하세요",
    chainOfThought: "사고 과정",
    close: "닫기",
    source: "출처",
    image: "이미지",
    attachment: "첨부 파일",
    remove: "삭제",
    toggle: "펼치기/접기",
    previousVersion: "이전 버전",
    nextVersion: "다음 버전",
    removeAttachment: "첨부 파일 삭제",
    thinking: "생각 중…",
    thought: "깊이 생각함",
    thoughtFor: (seconds: number) => `${seconds}초 동안 깊이 생각함`,
    urlPlaceholder: "URL 입력…",
    preview: "미리보기",
    console: "콘솔",
    noOutput: "출력 없음",
    unknownSource: "알 수 없는 출처",
    previous: "이전",
    next: "다음",
    contextUsage: "컨텍스트 사용량",
    totalCost: "총비용",
    input: "입력",
    output: "출력",
    reasoning: "추론",
    cacheHit: "캐시 적중",
    uploadFile: "파일 업로드",
    promptPlaceholder: "무엇이 궁금하신가요?",
    send: "보내기",
    selectModel: "모델 선택",
    toolStatus: {
      "input-streaming": "준비 중",
      "input-available": "실행 중",
      "approval-requested": "승인 대기",
      "approval-responded": "승인됨",
      "output-available": "완료",
      "output-error": "실패",
      "output-denied": "거부됨",
    },
    toolParameters: "매개변수",
    toolError: "오류",
    toolResult: "결과",
    sourcesUsed: (count: number) => `출처 ${count}개 참고`,
  },
  de: {
    emptyTitle: "Noch keine Nachrichten",
    emptyDescription: "Senden Sie eine Nachricht, um das Gespräch zu beginnen",
    chainOfThought: "Gedankengang",
    close: "Schließen",
    source: "Quelle",
    image: "Bild",
    attachment: "Anhang",
    remove: "Entfernen",
    toggle: "Aus- oder einklappen",
    previousVersion: "Vorherige Version",
    nextVersion: "Nächste Version",
    removeAttachment: "Anhang entfernen",
    thinking: "Denkt nach…",
    thought: "Gründlich nachgedacht",
    thoughtFor: (seconds: number) => `Gründlich nachgedacht (${seconds} s)`,
    urlPlaceholder: "URL eingeben…",
    preview: "Vorschau",
    console: "Konsole",
    noOutput: "Keine Ausgabe",
    unknownSource: "Unbekannte Quelle",
    previous: "Zurück",
    next: "Weiter",
    contextUsage: "Kontextnutzung",
    totalCost: "Gesamtkosten",
    input: "Eingabe",
    output: "Ausgabe",
    reasoning: "Schlussfolgerung",
    cacheHit: "Davon aus dem Cache",
    uploadFile: "Datei hochladen",
    promptPlaceholder: "Was möchten Sie wissen?",
    send: "Senden",
    selectModel: "Modell auswählen",
    toolStatus: {
      "input-streaming": "Wird vorbereitet",
      "input-available": "Wird ausgeführt",
      "approval-requested": "Wartet auf Bestätigung",
      "approval-responded": "Bestätigt",
      "output-available": "Abgeschlossen",
      "output-error": "Fehlgeschlagen",
      "output-denied": "Abgelehnt",
    },
    toolParameters: "Parameter",
    toolError: "Fehler",
    toolResult: "Ergebnis",
    sourcesUsed: (count: number) => `${count} Quellen verwendet`,
  },
  fr: {
    emptyTitle: "Aucun message pour l'instant",
    emptyDescription: "Envoyez un message pour démarrer la conversation",
    chainOfThought: "Raisonnement",
    close: "Fermer",
    source: "Source",
    image: "Image",
    attachment: "Pièce jointe",
    remove: "Retirer",
    toggle: "Développer ou réduire",
    previousVersion: "Version précédente",
    nextVersion: "Version suivante",
    removeAttachment: "Retirer la pièce jointe",
    thinking: "Réflexion…",
    thought: "Réflexion approfondie",
    thoughtFor: (seconds: number) => `Réflexion approfondie (${seconds} s)`,
    urlPlaceholder: "Saisissez une URL…",
    preview: "Aperçu",
    console: "Console",
    noOutput: "Aucune sortie",
    unknownSource: "Source inconnue",
    previous: "Précédent",
    next: "Suivant",
    contextUsage: "Utilisation du contexte",
    totalCost: "Coût total",
    input: "Entrée",
    output: "Sortie",
    reasoning: "Raisonnement",
    cacheHit: "Dont cache",
    uploadFile: "Importer un fichier",
    promptPlaceholder: "Que voulez-vous savoir ?",
    send: "Envoyer",
    selectModel: "Choisir un modèle",
    toolStatus: {
      "input-streaming": "Préparation",
      "input-available": "En cours",
      "approval-requested": "En attente de validation",
      "approval-responded": "Validé",
      "output-available": "Terminé",
      "output-error": "Échec",
      "output-denied": "Refusé",
    },
    toolParameters: "Paramètres",
    toolError: "Erreur",
    toolResult: "Résultat",
    sourcesUsed: (count: number) => `${count} source(s) utilisée(s)`,
  },
  es: {
    emptyTitle: "Aún no hay mensajes",
    emptyDescription: "Envíe un mensaje para iniciar la conversación",
    chainOfThought: "Razonamiento",
    close: "Cerrar",
    source: "Fuente",
    image: "Imagen",
    attachment: "Adjunto",
    remove: "Quitar",
    toggle: "Expandir o contraer",
    previousVersion: "Versión anterior",
    nextVersion: "Versión siguiente",
    removeAttachment: "Quitar adjunto",
    thinking: "Pensando…",
    thought: "Razonamiento profundo",
    thoughtFor: (seconds: number) => `Razonamiento profundo (${seconds} s)`,
    urlPlaceholder: "Introduzca una URL…",
    preview: "Vista previa",
    console: "Consola",
    noOutput: "Sin salida",
    unknownSource: "Fuente desconocida",
    previous: "Anterior",
    next: "Siguiente",
    contextUsage: "Uso del contexto",
    totalCost: "Costo total",
    input: "Entrada",
    output: "Salida",
    reasoning: "Razonamiento",
    cacheHit: "De ellos, en caché",
    uploadFile: "Subir archivo",
    promptPlaceholder: "¿Qué desea preguntar?",
    send: "Enviar",
    selectModel: "Seleccionar modelo",
    toolStatus: {
      "input-streaming": "Preparando",
      "input-available": "En ejecución",
      "approval-requested": "Pendiente de aprobación",
      "approval-responded": "Aprobado",
      "output-available": "Completado",
      "output-error": "Error",
      "output-denied": "Rechazado",
    },
    toolParameters: "Parámetros",
    toolError: "Error",
    toolResult: "Resultado",
    sourcesUsed: (count: number) => `${count} fuente(s) consultada(s)`,
  },
  ru: {
    emptyTitle: "Сообщений пока нет",
    emptyDescription: "Отправьте сообщение, чтобы начать диалог",
    chainOfThought: "Ход рассуждений",
    close: "Закрыть",
    source: "Источник",
    image: "Изображение",
    attachment: "Вложение",
    remove: "Удалить",
    toggle: "Развернуть или свернуть",
    previousVersion: "Предыдущая версия",
    nextVersion: "Следующая версия",
    removeAttachment: "Удалить вложение",
    thinking: "Думаю…",
    thought: "Глубокое размышление",
    thoughtFor: (seconds: number) => `Глубокое размышление (${seconds} с)`,
    urlPlaceholder: "Введите URL…",
    preview: "Предпросмотр",
    console: "Консоль",
    noOutput: "Нет вывода",
    unknownSource: "Неизвестный источник",
    previous: "Назад",
    next: "Далее",
    contextUsage: "Использование контекста",
    totalCost: "Итоговая стоимость",
    input: "Ввод",
    output: "Вывод",
    reasoning: "Рассуждение",
    cacheHit: "Из кэша",
    uploadFile: "Загрузить файл",
    promptPlaceholder: "Что вы хотите спросить?",
    send: "Отправить",
    selectModel: "Выбрать модель",
    toolStatus: {
      "input-streaming": "Подготовка",
      "input-available": "Выполняется",
      "approval-requested": "Ожидает подтверждения",
      "approval-responded": "Подтверждено",
      "output-available": "Готово",
      "output-error": "Ошибка",
      "output-denied": "Отклонено",
    },
    toolParameters: "Параметры",
    toolError: "Ошибка",
    toolResult: "Результат",
    sourcesUsed: (count: number) => `Использовано источников: ${count}`,
  },
  pt: {
    emptyTitle: "Ainda não há mensagens",
    emptyDescription: "Envie uma mensagem para começar a conversa",
    chainOfThought: "Raciocínio",
    close: "Fechar",
    source: "Fonte",
    image: "Imagem",
    attachment: "Anexo",
    remove: "Remover",
    toggle: "Expandir ou recolher",
    previousVersion: "Versão anterior",
    nextVersion: "Próxima versão",
    removeAttachment: "Remover anexo",
    thinking: "Pensando…",
    thought: "Raciocínio profundo",
    thoughtFor: (seconds: number) => `Raciocínio profundo (${seconds} s)`,
    urlPlaceholder: "Digite uma URL…",
    preview: "Pré-visualização",
    console: "Console",
    noOutput: "Sem saída",
    unknownSource: "Fonte desconhecida",
    previous: "Anterior",
    next: "Próximo",
    contextUsage: "Uso do contexto",
    totalCost: "Custo total",
    input: "Entrada",
    output: "Saída",
    reasoning: "Raciocínio",
    cacheHit: "Do cache",
    uploadFile: "Enviar arquivo",
    promptPlaceholder: "O que você quer perguntar?",
    send: "Enviar",
    selectModel: "Selecionar modelo",
    toolStatus: {
      "input-streaming": "Preparando",
      "input-available": "Executando",
      "approval-requested": "Aguardando aprovação",
      "approval-responded": "Aprovado",
      "output-available": "Concluído",
      "output-error": "Falhou",
      "output-denied": "Negado",
    },
    toolParameters: "Parâmetros",
    toolError: "Erro",
    toolResult: "Resultado",
    sourcesUsed: (count: number) => `${count} fonte(s) consultada(s)`,
  },
};

export function useAiElementsCopy(): AiElementsCopy {
  const locale = useLocale();

  return COPY[locale as UiLanguage] ?? COPY.zh;
}
